import React, { Suspense, useEffect, useRef } from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Box } from '@mui/material';
import AppBarComponent from './components/AppBar/AppBarComponent';
import LoadingDots from './components/LoadingDots/LoadingDots';
import Home from './screens/home/Home';
import { RESULT_PAGES } from './screens/results_page/resultPages';
import { MobileProvider } from './contexts/MobileContext';
import { setupGlobalShortcuts } from './utils/globalShortcuts';
import lazyWithReload from './utils/lazyWithReload';
import { LANDING_PATHS } from './utils/landingPaths';
import {
  isValidatedWithin,
  ENABLE_OTP_SMS,
} from './utils/questionEngineOtpHelpers';
import { FLOW_SLUG_CONFIG } from './utils/flowSlugConfig';
import { toGtmProduct, pushDataLayer } from './utils/dataLayer';
import { getBaseProductRoute } from './utils/productNavigation';
import SessionManager from './utils/sessionManager';
import { canonicalizeTrailingSlash } from './utils/canonicalPath';
import {
  applyPendingRemoteConfig,
  maybeRefetchRemoteConfig,
} from './experiments/remoteConfig';

// Landing pages (Home) are in the main bundle so they render as soon as it
// runs. Every other screen is its own chunk, loaded when its route is first
// visited, so a landing visit doesn't download the quote flow, results pages
// and blog up front. The question engine is where every landing CTA leads, so
// the browser prefetches it once the page is idle. Results screens live in
// screens/results_page/resultPages.js, so the question engine can preload the
// one its flow ends on.
const QuestionEngine = lazyWithReload(
  () =>
    import(
      /* webpackPrefetch: true */ './screens/question_engine/QuestionEngine'
    )
);
const Accounts = lazyWithReload(() => import('./screens/account/Accounts'));
const QuoteSelected = lazyWithReload(
  () => import('./screens/quote_selected/QuoteSelected')
);
const PersonalLoansThankYou = lazyWithReload(
  () => import('./screens/quote_selected/PersonalLoansThankYou')
);
const FlexicareThankYou = lazyWithReload(
  () => import('./screens/quote_selected/FlexicareThankYou')
);
const ExternalQuoteAccept = lazyWithReload(
  () => import('./screens/quote_selected/ExternalQuoteAccept')
);
const BlogPost = lazyWithReload(() => import('./screens/blog/BlogPost'));
const BlogList = lazyWithReload(() => import('./screens/blog/BlogList'));
const SpinOff = lazyWithReload(() => import('./screens/spin-off/SpinOff'));
const NotFound = lazyWithReload(() => import('./screens/404/NotFound'));
const Gone = lazyWithReload(() => import('./screens/410/Error'));
const DocumentPage = lazyWithReload(
  () => import('./screens/document/DocumentPage')
);
const About = lazyWithReload(() => import('./screens/about/About'));
const ContactUs = lazyWithReload(
  () => import('./screens/contact-us/ContactUs')
);

// Applies a remote experiment document fetched since the last navigation,
// before the new route renders, so an arm never switches mid-render. It must
// stay the first child of the Router: siblings render in order, and everything
// after it reads the applied config.
function ExperimentsSync() {
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  if (lastPathname.current !== pathname) {
    lastPathname.current = pathname;
    applyPendingRemoteConfig();
  }

  // Then start a fresh fetch for the navigation after this one, throttled so
  // a burst of clicks cannot stampede. In an effect, not in render: this is a
  // side effect, and the value it collects is deliberately not for this page.
  useEffect(() => {
    maybeRefetchRemoteConfig();
  }, [pathname]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function VdnCapture() {
  const { search } = useLocation();
  useEffect(() => {
    SessionManager.captureVdnFromQueryString(search);
  }, [search]);
  return null;
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add('animations-enabled');
    return () => {
      document.documentElement.classList.remove('animations-enabled');
    };
  }, []);

  return (
    <MobileProvider>
      <Router>
        <ExperimentsSync />
        <ScrollToTop />
        <VdnCapture />
        <GTMPageTracker />
        <GlobalShortcutsWrapper />
        <ConditionalAppBar />
        {/* Navigations are transitions (BrowserRouter wraps them in
            startTransition), so while a screen's chunk downloads the current
            page stays on screen; the fallback only shows when a lazy screen is
            the first thing rendered after a full page load. */}
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {LANDING_PATHS.map((path) => (
              <Route key={path} path={path} element={<ProductLanding />} />
            ))}

            <Route path="/more/:slug" element={<DocumentPage />} />
            <Route
              path="/check-fibre-in-your-area"
              element={
                <Navigate to="/check-fibre-in-your-area/step1" replace />
              }
            />
            <Route
              path="/compare-fibre-deals/:step/*"
              element={<FibreLegacyStepRedirect />}
            />
            <Route
              path="/business-insurance-quote/:flowSlug/:step/*"
              element={<FlowRouteResolver prefix="business-insurance-quote" />}
            />
            <Route path="/:flowSlug/:step/*" element={<FlowRouteResolver />} />
            <Route path="/:spin-off-slug" element={<SpinOff />} />
            {Object.entries(RESULT_PAGES).map(([path, ResultScreen]) => (
              <Route key={path} path={path} element={<ResultScreen />} />
            ))}
            <Route path="/accounts" element={<AccountsRoute />} />
            <Route path="/quoteSelection" element={<QuoteSelectionRoute />} />
            <Route
              path="/personal-loans/thankyou"
              element={<PersonalLoansThankYou />}
            />
            <Route
              path="/flexicare-thank-you"
              element={<FlexicareThankYou />}
            />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/page/:page" element={<BlogList />} />
            <Route path="/blog/:category/" element={<BlogList />} />
            <Route path="/blog/:category/page/:page" element={<BlogList />} />
            <Route path="/blog/:category/:slug" element={<BlogPost />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/410-gone" element={<Gone />} />

            {/* 404 Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </MobileProvider>
  );
}

// Product landing pages double as external quote acceptance entry points. When a
// shared quote link lands here with the quote/person keys in the query string,
// accept the quote and forward to the thank you page; otherwise render the page.
function ProductLanding() {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  // These routes are matched with or without a trailing slash (and content
  // renders the same either way via normalizePathname in Home.js), so force
  // the URL itself to a single canonical trailing slash.
  useEffect(() => {
    const canonicalPathname = canonicalizeTrailingSlash(pathname);
    if (pathname !== canonicalPathname) {
      navigate(`${canonicalPathname}${search}${hash}`, { replace: true });
    }
  }, [pathname, search, hash, navigate]);

  const params = new URLSearchParams(search);
  const quoteId = params.get('quoteId');
  const personId = params.get('personId');

  if (quoteId && personId) {
    return <ExternalQuoteAccept quoteId={quoteId} personId={personId} />;
  }

  return <Home />;
}

function ConditionalAppBar() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/more/')) return null;
  return <AppBarComponent loggedIn={false} />;
}

function RouteFallback() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoadingDots variant="otp" />
    </Box>
  );
}

function GTMPageTracker() {
  const location = useLocation();

  useEffect(() => {
    const flowSlug = getBaseProductRoute(location.pathname).replace(/^\//, '');
    const flowConfig = FLOW_SLUG_CONFIG[flowSlug];
    const product = flowConfig ? toGtmProduct(flowConfig.product) : flowSlug;

    pushDataLayer({
      event: 'pageview',
      page: location.pathname + location.search,
      ...(product && { product }),
    });
  }, [location]);

  return null;
}

function GlobalShortcutsWrapper() {
  const navigate = useNavigate();

  useEffect(() => {
    const cleanup = setupGlobalShortcuts(navigate);
    return cleanup;
  }, [navigate]);

  return null;
}

// Old Fibre quote-flow URLs (/compare-fibre-deals/stepN/...) now live under
// /check-fibre-in-your-area/stepN/... — preserve the step + any trailing
// splat/query so in-progress links and indexed pages land on the right step.
function FibreLegacyStepRedirect() {
  const location = useLocation();
  const target =
    location.pathname.replace(
      '/compare-fibre-deals',
      '/check-fibre-in-your-area'
    ) + location.search;

  return <Navigate to={target} replace />;
}

function FlowRouteResolver({ prefix }) {
  const params = useParams();
  const step = params.step || '';
  const splat = params['*'] || '';
  const isQuestionStep = /^step-?\d+$/.test(step) && splat.length === 0;

  return isQuestionStep ? (
    <QuestionEngine flowSlugPrefix={prefix} />
  ) : (
    <SpinOff />
  );
}

function AccountsRoute() {
  const location = useLocation();

  if (!ENABLE_OTP_SMS || !isValidatedWithin()) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Accounts />;
}

// The selected quote only travels in router state, which a fresh load of
// /quoteSelection (address-bar suggestion, bookmark, restored tab) doesn't
// have. The quote was already accepted on the original visit, and showing it
// again would accept it a second time, so send these visitors home.
function QuoteSelectionRoute() {
  const location = useLocation();

  if (!location.state) {
    return <Navigate to="/" replace />;
  }

  return <QuoteSelected />;
}

export default App;
